/**
 * navigationTracker.js — Tracks navigation breadcrumbs and last-clicked elements.
 * Stores data in sessionStorage so it survives React re-renders but not tab close.
 * Used by NotFound to provide context about what led to a 404.
 */

const BREADCRUMB_KEY = "nav_breadcrumbs";
const CLICK_KEY = "nav_last_click";
const MAX_BREADCRUMBS = 10;

/**
 * Record a page visit. Call this on every route change.
 */
export function recordPageVisit(path) {
  try {
    const raw = sessionStorage.getItem(BREADCRUMB_KEY);
    const crumbs = raw ? JSON.parse(raw) : [];

    // Don't duplicate consecutive visits to the same path
    if (crumbs.length > 0 && crumbs[crumbs.length - 1].path === path) return;

    crumbs.push({ path, time: Date.now() });

    // Keep only the last N entries
    if (crumbs.length > MAX_BREADCRUMBS) {
      crumbs.splice(0, crumbs.length - MAX_BREADCRUMBS);
    }

    sessionStorage.setItem(BREADCRUMB_KEY, JSON.stringify(crumbs));
  } catch {
    // sessionStorage unavailable — fail silently
  }
}

/**
 * Record the last meaningful click (links, buttons).
 * Attach this as a global click listener on document.
 */
export function handleGlobalClick(e) {
  try {
    // Walk up the DOM to find the nearest <a> or <button>
    let el = e.target;
    let depth = 0;
    while (el && depth < 6) {
      const tag = el.tagName?.toLowerCase();
      if (tag === "a" || tag === "button") break;
      // Also catch elements with role="link" or role="button"
      const role = el.getAttribute?.("role");
      if (role === "link" || role === "button") break;
      el = el.parentElement;
      depth++;
    }

    if (!el || !el.tagName) return;

    const tag = el.tagName.toLowerCase();
    const role = el.getAttribute?.("role");
    if (tag !== "a" && tag !== "button" && role !== "link" && role !== "button") return;

    // Extract useful info
    const text = (el.textContent || "").trim().slice(0, 100);
    const href = el.getAttribute("href") || el.getAttribute("to") || null;
    const ariaLabel = el.getAttribute("aria-label") || null;
    const currentPage = window.location.pathname;

    const clickInfo = {
      text: text || ariaLabel || "(no text)",
      href,
      tag,
      page: currentPage,
      time: Date.now(),
    };

    sessionStorage.setItem(CLICK_KEY, JSON.stringify(clickInfo));
  } catch {
    // Fail silently
  }
}

/**
 * Get the full navigation context for error reporting.
 * Returns { breadcrumbs, lastClick, previousPage }
 */
export function getNavigationContext() {
  try {
    const rawCrumbs = sessionStorage.getItem(BREADCRUMB_KEY);
    const rawClick = sessionStorage.getItem(CLICK_KEY);

    const breadcrumbs = rawCrumbs ? JSON.parse(rawCrumbs) : [];
    const lastClick = rawClick ? JSON.parse(rawClick) : null;

    // Previous page = second-to-last breadcrumb (last one is the 404 page itself)
    const previousPage =
      breadcrumbs.length >= 2
        ? breadcrumbs[breadcrumbs.length - 2].path
        : null;

    return { breadcrumbs, lastClick, previousPage };
  } catch {
    return { breadcrumbs: [], lastClick: null, previousPage: null };
  }
}
